import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { spaceApi } from '../services/api';
import { useSpaceId } from '../contexts/SpaceContext';
import type {
  EventSubscription,
  ListSubscriptionsParams,
  SubscriptionStatus,
} from '../types';

function useSubscriptionApi() {
  const spaceId = useSpaceId();
  return useMemo(() => spaceApi(spaceId).subscriptions, [spaceId]);
}

export function useSubscriptions(params?: ListSubscriptionsParams) {
  const spaceId = useSpaceId();
  const api = useSubscriptionApi();
  return useQuery({
    queryKey: ['space', spaceId, 'subscriptions', params],
    queryFn: () => api.list(params),
  });
}

export function useUpdateSubscription() {
  const spaceId = useSpaceId();
  const api = useSubscriptionApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      subscriptionId,
      status,
    }: {
      subscriptionId: string;
      status: SubscriptionStatus;
    }) => api.update(subscriptionId, { status }),
    onSuccess: (updated) => {
      queryClient.setQueriesData<{ items: EventSubscription[] }>(
        { queryKey: ['space', spaceId, 'subscriptions'] },
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            items: prev.items.map((s) =>
              s.subscriptionId === updated.subscriptionId
                ? { ...updated, subscriberName: s.subscriberName }
                : s,
            ),
          };
        },
      );
    },
  });
}
